import { ComponentType, Fragment } from 'react';

import { FilterListItem } from '@components/filter-list-item';
import { IconArrowLeft } from '@components/icons/arrow-left';

import { FilterConfig, FilterConfigField, FilterConfigType, FilterData } from './common';
import {
  inlineComparers,
  inlineTypes,
  resolveAutoComparer,
  useFilterTranslate,
} from './filter-utils';
import { FilterListSeparator } from './list-separator';
import { BooleanMenu } from './menus/boolean-menu';
import { RELATIVE_COMPARERS, DateMenu } from './menus/date-menu';
import { EmailMenu } from './menus/email-menu';
import { FileMenu } from './menus/file-menu';
import { IssuesMenu } from './menus/issues-menu';
import { JsonMenu } from './menus/json-menu';
import { DecimalMenu, NumberMenu } from './menus/number-menu';
import { ParagraphMenu } from './menus/paragraph-menu';
import { PhoneMenu } from './menus/phone-menu';
import { ProgressMenu } from './menus/progress-menu';
import { SelectMenu } from './menus/select-menu';
import { TextMenu } from './menus/text-menu';
import { MenuProps } from './menus/types';
import { UserMenu } from './menus/user-menu';
import { CurrentFilterActions, CurrentFilterState, useCurrentFilter } from './use-current-filter';

import { FilterActions } from '../table-store';

const getCurrentConfig = (category: FilterConfigField, fieldId?: string): FilterConfigField => {
  if (category.type === 'category' && fieldId) {
    const fieldConfig = category.fields.find((field) => field.id === fieldId);
    return fieldConfig ?? category;
  }
  return category;
};

type FilterListMenuProps = {
  onSelect(category: string): void;
  state: CurrentFilterState;
  actions: CurrentFilterActions;
  config: FilterConfig;
  hideNavigation?: boolean;
  onBack?: () => void;
  backLabel?: string;
};

const MENU_COMPONENTS: Record<FilterConfigType, ComponentType<MenuProps> | null> = {
  date: DateMenu,
  datetime: DateMenu,
  progress: ProgressMenu,
  text: TextMenu,
  paragraph: ParagraphMenu,
  email: EmailMenu,
  phone_number: PhoneMenu,
  single_select: SelectMenu,
  multi_select: SelectMenu,
  user: UserMenu,
  boolean: BooleanMenu,
  number: NumberMenu,
  decimal: DecimalMenu,
  issues: IssuesMenu,
  category: null,
  separator: null,
  custom: null,
  quick_filters: null,
  json: JsonMenu,
  file: FileMenu,
  file_multi: FileMenu,
};

export function FilterListMenu({
  config,
  onSelect,
  state,
  actions,
  hideNavigation,
  onBack,
  backLabel,
}: FilterListMenuProps) {
  const { t } = useFilterTranslate();

  const handleCategorySelect = (categoryId: string) => {
    const category = config.find((cfg) => cfg.id === categoryId);
    if (!category || category.type === 'separator') return;

    onSelect(categoryId);

    const autoComparer = resolveAutoComparer(config, categoryId);
    if (autoComparer) {
      actions.selectComparer(autoComparer);
    }
  };

  if (!state.category) {
    return (
      <Fragment>
        {config.map((cfg, index) => {
          if (cfg.type === 'separator') {
            return <FilterListSeparator key={index} />;
          }
          if (cfg.type === 'quick_filters') {
            return (
              <Fragment key={index}>
                {cfg.quick.map((quickFilter, quickIndex) => (
                  <FilterListItem
                    key={`quick-${quickIndex}`}
                    onClick={() => {
                      actions.applyFilter({
                        id: quickFilter.id,
                        category: quickFilter.apply.category!,
                        comparer: quickFilter.apply.comparer!,
                        value: quickFilter.apply.value!,
                      });
                    }}
                    icon={quickFilter.icon}
                    label={quickFilter.label}
                  />
                ))}
                <FilterListSeparator />
              </Fragment>
            );
          }
          return (
            <FilterListItem
              key={index}
              onClick={() => handleCategorySelect(cfg.id)}
              icon={'icon' in cfg ? cfg.icon : undefined}
              label={'label' in cfg ? cfg.label : ''}
            />
          );
        })}
      </Fragment>
    );
  }

  const category = config.find((cfg) => cfg.id === state.category);
  if (!category) return null;

  const currentConfig = getCurrentConfig(category, state.field);
  if (currentConfig.type === 'separator') return null;

  const renderHeader = () => {
    const hasOnlyOneComparer =
      'allowedComparers' in currentConfig &&
      currentConfig.allowedComparers &&
      currentConfig.allowedComparers.length === 1;

    const showBackBreadcrumb = Boolean(onBack);
    const showCategoryBreadcrumb = !hideNavigation;
    const showFieldBreadcrumb = !hideNavigation && Boolean(state.field);
    const showComparerBreadcrumb = Boolean(
      state.comparer &&
      !RELATIVE_COMPARERS.includes(state.comparer) &&
      !inlineTypes.includes(currentConfig.type) &&
      !inlineComparers.includes(state.comparer) &&
      !hasOnlyOneComparer,
    );

    if (
      !showBackBreadcrumb &&
      !showCategoryBreadcrumb &&
      !showFieldBreadcrumb &&
      !showComparerBreadcrumb
    ) {
      return null;
    }

    return (
      <Fragment>
        {showBackBreadcrumb && (
          <FilterListItem
            isHeading
            onClick={onBack!}
            icon={<IconArrowLeft />}
            label={backLabel ?? ''}
          />
        )}
        {showCategoryBreadcrumb && (
          <FilterListItem
            isHeading
            onClick={actions.unselectCategory}
            icon={<IconArrowLeft />}
            label={category.type !== 'separator' && 'label' in category ? category.label : ''}
          />
        )}
        {showFieldBreadcrumb && (
          <FilterListItem
            isHeading
            onClick={actions.unselectField}
            icon={<IconArrowLeft />}
            label={'label' in currentConfig ? currentConfig.label : ''}
          />
        )}
        {showComparerBreadcrumb && state.comparer && (
          <FilterListItem
            isHeading
            onClick={actions.unselectComparer}
            icon={<IconArrowLeft />}
            label={t(`core.comparator.${state.comparer}`)}
          />
        )}
        <FilterListSeparator />
      </Fragment>
    );
  };

  const renderQuickFilters = () => {
    if (!state.value && !state.comparer && 'quick' in currentConfig && currentConfig.quick) {
      return (
        <Fragment>
          {currentConfig.quick.map((quickField, index) => (
            <FilterListItem
              key={index}
              label={quickField.label}
              onClick={() =>
                actions.applyFilter({
                  category: currentConfig.id,
                  ...quickField.apply,
                })
              }
              icon={quickField.icon}
            />
          ))}
          <FilterListSeparator />
        </Fragment>
      );
    }
    return null;
  };

  const handleFieldSelect = (fieldId: string) => {
    if (currentConfig.type !== 'category') return;

    const field = currentConfig.fields.find((candidate) => candidate.id === fieldId);
    if (!field || field.type === 'separator') return;

    actions.selectField(fieldId);

    const autoComparer = resolveAutoComparer(config, currentConfig.id, fieldId);
    if (autoComparer) {
      actions.selectComparer(autoComparer);
    }
  };

  const renderCategoryFields = () => {
    if (currentConfig.type === 'category' && !state.field) {
      return currentConfig.fields.map((field, index) => {
        if (field.type === 'separator') {
          return <FilterListSeparator key={field.id + index} />;
        }
        return (
          <FilterListItem
            key={field.id}
            label={field.label}
            onClick={() => handleFieldSelect(field.id)}
            icon={field.icon}
          />
        );
      });
    }
    return null;
  };

  const renderTypeMenu = () => {
    if (currentConfig.type === 'custom' && 'customMenu' in currentConfig) {
      const CustomMenuComponent = currentConfig.customMenu;
      return <CustomMenuComponent state={state} actions={actions} config={currentConfig} />;
    }

    const MenuComponent = MENU_COMPONENTS[currentConfig.type];
    if (MenuComponent) {
      return <MenuComponent state={state} actions={actions} config={currentConfig} />;
    }
    return null;
  };

  return (
    <Fragment>
      {renderHeader()}
      {renderQuickFilters()}
      {renderCategoryFields()}
      {renderTypeMenu()}
    </Fragment>
  );
}

export function FilterContent({
  initialData,
  closeMenu,
  config,
  actions: collectionActions,
  hideNavigation,
  onBack,
  backLabel,
}: {
  initialData?: Partial<FilterData>;
  closeMenu(): void;
  config: FilterConfig;
  actions: FilterActions;
  hideNavigation?: boolean;
  onBack?: () => void;
  backLabel?: string;
}) {
  const [state, actions] = useCurrentFilter({
    closeMenu,
    initialData,
    config,
    collectionActions,
  });

  return (
    <FilterListMenu
      config={config}
      onSelect={actions.selectCategory}
      state={state}
      actions={actions}
      hideNavigation={hideNavigation}
      onBack={onBack}
      backLabel={backLabel}
    />
  );
}
